import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, Tag } from 'antd'
import { CheckOutlined, DownOutlined, UpOutlined, WarningOutlined } from '@ant-design/icons'

import { formatTimeRange } from './journeyTime'
import { channelKey, reasonKey, wordsFor } from './journeyWords'
import {
  CHECKOUT_FAILED,
  minutesBetween,
  visitProducts,
  visitResultOf,
  visitSearches,
  visitTotals
} from './journeyFacts'
import { StepRow } from './StepRow'
import { Clock, InnerSteps, Muted, ProductLine, ProductName, StepBody, StepGrid, StepLine } from './journeyStyles'

const PRODUCTS_SHOWN = 3
const INNER_STEPS_SHOWN = 20
const SEARCHES_SHOWN = 3
const DIRECT = 'direct'

const VisitSource = ({ visit, journey, lang }) => {
  if (!visit.channel) return <Tag style={{ marginInlineEnd: 0 }}>{visit.source === 'app' ? 'app' : wordsFor(journey, 'visit', lang)}</Tag>
  const words = wordsFor(journey, channelKey(visit.channel), lang)
  if (visit.channel === DIRECT) return <Muted>{words}</Muted>
  return (
    <Tag color='geekblue' style={{ marginInlineEnd: 0 }}>
      {[words, visit.campaign].filter(Boolean).join(' · ')}
    </Tag>
  )
}

const VisitResult = ({ visit, journey, lang }) => {
  const { t } = useTranslation('inquiries')
  const result = visitResultOf(visit)
  if (!result) return null
  if (result !== 'checkoutFailed') return <span>{t(`orderJourney.result.${result}`)}</span>
  const reasons = (visit.steps || [])
    .filter((step) => step.name === CHECKOUT_FAILED)
    .flatMap((step) => step.details || [])
    .map((code) => wordsFor(journey, reasonKey(code), lang))
  return (
    <span>
      <WarningOutlined style={{ color: 'var(--journey-warning)', marginRight: 4 }} />
      {[t('orderJourney.result.checkoutFailed'), [...new Set(reasons)].join(', ')].filter(Boolean).join(': ')}
    </span>
  )
}

const VisitProducts = ({ visit, journey, orderIds }) => {
  const { t } = useTranslation('inquiries')
  const [showAll, setShowAll] = useState(false)
  const products = visitProducts(visit, journey.items, orderIds)
  if (!products.length) return null
  const shown = showAll ? products : products.slice(0, PRODUCTS_SHOWN)
  return (
    <ProductLine>
      {shown.map((product) => (
        <ProductName key={product.id} $inOrder={product.inOrder}>
          {product.inOrder && (
            <CheckOutlined aria-label={t('orderJourney.inOrder')} style={{ color: 'var(--journey-success)' }} />
          )}
          {product.name || `#${product.id}`}
          {product.carted && !product.inOrder && <Muted>({t('orderJourney.carted')})</Muted>}
        </ProductName>
      ))}
      {products.length > PRODUCTS_SHOWN && (
        <Button type='link' size='small' style={{ padding: 0 }} onClick={() => setShowAll(!showAll)}>
          {showAll ? t('orderJourney.less') : t('orderJourney.more', { n: products.length - PRODUCTS_SHOWN })}
        </Button>
      )}
    </ProductLine>
  )
}

const VisitRow = ({ visit, journey, lang, orderIds }) => {
  const { t } = useTranslation('inquiries')
  const [open, setOpen] = useState(false)
  const [allInner, setAllInner] = useState(false)
  const minutes = minutesBetween(visit.first_at, visit.last_at)
  const searches = visitSearches(visit)
  const totals = visitTotals(visit)
    .map((total) => `${wordsFor(journey, total.name, lang)} ${total.count}`)
    .join(' · ')
  const inner = visit.steps || []
  const shownInner = allInner ? inner : inner.slice(0, INNER_STEPS_SHOWN)
  const loneStepWithoutResult = inner.length === 1 && !visitResultOf(visit)

  return (
    <StepGrid>
      <Clock>{formatTimeRange(visit.first_at, visit.last_at)}</Clock>
      <StepBody>
        <StepLine>
          <VisitSource visit={visit} journey={journey} lang={lang} />
          {minutes > 0 && <Muted>{t('orderJourney.minutes', { n: minutes })}</Muted>}
          <VisitResult visit={visit} journey={journey} lang={lang} />
        </StepLine>
        <VisitProducts visit={visit} journey={journey} orderIds={orderIds} />
        {searches.length > 0 && (
          <Muted>
            {t('orderJourney.searched')}: {searches.slice(0, SEARCHES_SHOWN).map((text) => `“${text}”`).join(', ')}
            {searches.length > SEARCHES_SHOWN && ` ${t('orderJourney.more', { n: searches.length - SEARCHES_SHOWN })}`}
          </Muted>
        )}
        {inner.length > 1 && (
          <div>
            <Muted>{totals}</Muted>
            <Button
              type='link'
              size='small'
              aria-expanded={open}
              icon={open ? <UpOutlined /> : <DownOutlined />}
              iconPosition='end'
              onClick={() => setOpen(!open)}
            >
              {open ? t('orderJourney.hideSteps') : t('orderJourney.showSteps', { count: inner.length })}
            </Button>
          </div>
        )}
        {(open || loneStepWithoutResult) && (
          <InnerSteps>
            {shownInner.map((step, index) => (
              <StepRow
                key={`${step.name}-${step.first_at}-${index}`}
                step={step}
                journey={journey}
                lang={lang}
                orderIds={orderIds}
              />
            ))}
            {inner.length > INNER_STEPS_SHOWN && (
              <Button type='link' size='small' onClick={() => setAllInner(!allInner)}>
                {allInner ? t('orderJourney.less') : t('orderJourney.more', { n: inner.length - INNER_STEPS_SHOWN })}
              </Button>
            )}
          </InnerSteps>
        )}
      </StepBody>
    </StepGrid>
  )
}

VisitRow.propTypes = {
  visit: PropTypes.object.isRequired,
  journey: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired
}

export { VisitRow }
